import React, { useState,useRef, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import {
  AppBar, Toolbar, Typography, Container, CssBaseline, ThemeProvider,
  Box, TextField, Button, MenuItem, Divider, Paper, CircularProgress, Alert
} from '@mui/material';
import { createTheme } from '@mui/material/styles';
import Footer from '../pages/Footer';
import logo from '../logo.png';
import axios from 'axios';

const defaultTheme = createTheme({
  palette: {
    primary: { main: 'rgb(18,82,166)' },
    background: { default: '#eef2f5' },
  },
  typography: {
    fontFamily: '"Times New Roman", Times, serif',
  },
});

export default function ForgotUsernamePage() {
  const [firstName, setFirstName] = useState('');
  const [fathersName, setFathersName] = useState('');
  const [university, setUniversity] = useState('');
  const [department, setDepartment] = useState('');
  const [universities, setUniversities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [username, setUsername] = useState(null);
  const [usernames, setUsernames] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    axios.get('https://exam.ethernet.edu.et/api/universities')
      .then(res => setUniversities(res.data));
    axios.get('https://exam.ethernet.edu.et/api/departments')
      .then(res => setDepartments(res.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUsername(null);
    setUsernames([]);
    setMessage('');
    setError('');
    setLoading(true);
  
    try {
      // Get token from invisible reCAPTCHA
      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset(); // reset for next use
  
      const res = await axios.post('https://exam.ethernet.edu.et/api/forgot-username', {
        firstName,
        fathersName,
        university,
        department,
        recaptcha_token: token, 
      });
  
      // Handle multiple usernames response
      if (res.data?.usernames && Array.isArray(res.data.usernames)) {
        setUsernames(res.data.usernames);
        setMessage(res.data.message || 'Found matching records for your information.');
      } 
      // Handle single username response (backward compatibility)
      else if (res.data?.username) {
        setUsername(res.data.username);
      } 
      // Handle error response
      else if (res.data?.error) {
        setError(res.data.error);
      } else {
        setError('Profile not found. Please check your information.');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" elevation={1}>
          <Toolbar sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 } }}>
            <Box
              component="img"
              src={logo}
              alt="Ministry of Education Logo"
              sx={{
                maxHeight: { xs: '50px', sm: '70px' },
                marginRight: { xs: '10px', sm: '20px' },
                width: 'auto'
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1,
                fontSize: { xs: '0.9rem', sm: '1.25rem' }
              }}
            >
              Ministry of Education
            </Typography>
          </Toolbar>
        </AppBar>

        <Container component="main" sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', my: 6 }}>
          <Box sx={{ maxWidth: 520, width: '100%' }}>
            <Paper elevation={4} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
              <Typography variant="h5" align="center" gutterBottom>
                <b>Forgot Username</b>
              </Typography>
              <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
                Enter your academic info to recover your account username.
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <form onSubmit={handleSubmit}>
                <TextField fullWidth label="First Name" required value={firstName}
                  onChange={(e) => setFirstName(e.target.value)} sx={{ mb: 2 }} size="medium" />
                <TextField fullWidth label="Father's Name" required value={fathersName}
                  onChange={(e) => setFathersName(e.target.value)} sx={{ mb: 2 }} size="medium" />

                <TextField
                  select fullWidth required label="University" value={university}
                  onChange={(e) => setUniversity(e.target.value)} sx={{ mb: 2 }} size="medium"
                >
                  {universities.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select fullWidth required label="Department" value={department}
                  onChange={(e) => setDepartment(e.target.value)} sx={{ mb: 2 }} size="medium"
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </TextField>

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={28} />
                  </Box>
                )}

                {/* Display multiple usernames */}
                {!loading && usernames.length > 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {message && (
                      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                        {message}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      ✅ Found <strong>{usernames.length}</strong> username{usernames.length > 1 ? 's' : ''}:
                    </Typography>
                    <Box sx={{ 
                      mt: 1.5, 
                      p: 2, 
                      backgroundColor: 'rgba(255, 255, 255, 0.3)', 
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      {usernames.map((uname, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            p: 1.5, 
                            backgroundColor: 'white', 
                            borderRadius: 1,
                            border: '1px solid rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {uname}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1.5, fontSize: '0.875rem' }}>
                      Please make sure to <strong>note them down</strong> or <strong>save them securely</strong>.
                      You'll need one of these to log in and complete your registration.
                    </Typography>
                  </Alert>
                )}

                {/* Display single username (backward compatibility) */}
                {!loading && username && usernames.length === 0 && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Your username is: <strong>{username}</strong><br />
                    Please make sure to <strong>note it down</strong> or <strong>save it securely</strong>.
                    You'll need it to log in and complete your registration.
                  </Alert>
                )}

                {!loading && error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'stretch', sm: 'space-between' },
                gap: { xs: 2, sm: 2 },
                mt: 3 
              }}>
                <Button 
                  variant="contained" 
                  href="/" 
                  disabled={loading}
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ minWidth: { xs: 'auto', sm: 120 } }}
                >
                  Back
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={loading}
                  fullWidth={{ xs: true, sm: false }}
                  sx={{ minWidth: { xs: 'auto', sm: 120 } }}
                >
                  {loading ? 'Please wait...' : 'Get Username'}
                </Button>
              </Box>

              </form>
            </Paper>
          </Box>
        </Container>

        <Footer />
        <ReCAPTCHA
            sitekey="6LepLRQrAAAAAGsabsOe4cjcNnm61WG8fuFvuIUq"   
            size="invisible"
  ref={recaptchaRef}
/>

      </Box>
    </ThemeProvider>
  );
}
